import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import type { UserSuperAdminPage, UserDetailAdmin } from '@/types/superadmin';

export interface SuperAdminUsersQuery {
  search?: string;
  role?: string;
  status?: string;
  sort?: string;
  direction?: 'asc' | 'desc';
  page?: number;
  pageSize?: number;
}

export function useSuperAdminUsers(query: SuperAdminUsersQuery = {}) {
  return useQuery<UserSuperAdminPage>({
    queryKey: ['superadmin', 'users', query],
    queryFn: async () => {
      const params: Record<string, string> = {};
      if (query.search) params.search = query.search;
      if (query.role && query.role !== 'all') params.role = query.role;
      if (query.status && query.status !== 'all') params.status = query.status;
      if (query.sort) params.sort = query.sort;
      if (query.direction) params.direction = query.direction;
      if (query.page && query.page > 1) params.page = String(query.page);
      if (query.pageSize) params.page_size = String(query.pageSize);
      const { data } = await api.get<UserSuperAdminPage>('/superadmin/users', { params });
      return data;
    },
  });
}

export function useSuperAdminUserDetail(userId: string | null) {
  return useQuery<UserDetailAdmin>({
    queryKey: ['superadmin', 'user-detail', userId],
    queryFn: async () => {
      const { data } = await api.get(`/superadmin/users/${userId}`);
      return data;
    },
    enabled: Boolean(userId),
  });
}

export function useCreateSuperAdminUser() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (payload: {
      email: string;
      full_name: string;
      password: string;
      role?: string;
      phone?: string;
    }) => {
      const { data } = await api.post<UserDetailAdmin>('/superadmin/users', payload);
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['superadmin', 'users'] });
    },
  });
}

export function useUpdateSuperAdminUser() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      userId,
      payload,
    }: {
      userId: string;
      payload: Partial<{
        full_name: string;
        email: string;
        phone: string;
        role: string;
        status: string;
      }>;
    }) => {
      const { data } = await api.patch<UserDetailAdmin>(`/superadmin/users/${userId}`, payload);
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['superadmin'] });
    },
  });
}

export function useActivateSuperAdminUser() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (userId: string) => {
      const { data } = await api.post<UserDetailAdmin>(`/superadmin/users/${userId}/activate`);
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['superadmin'] });
    },
  });
}

export function useDeactivateSuperAdminUser() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (userId: string) => {
      const { data } = await api.post<UserDetailAdmin>(`/superadmin/users/${userId}/deactivate`);
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['superadmin'] });
    },
  });
}

export function useAdminResetPassword() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ userId, new_password }: { userId: string; new_password: string }) => {
      const { data } = await api.post(`/superadmin/users/${userId}/reset-password`, {
        new_password,
      });
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['superadmin'] });
    },
  });
}

export function useDeleteSuperAdminUser() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ userId, confirm }: { userId: string; confirm?: boolean }) => {
      const params = confirm ? '?confirm=true' : '';
      const { data } = await api.delete(`/superadmin/users/${userId}${params}`);
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['superadmin'] });
    },
  });
}

export function useReactivateSuperAdminUser() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (userId: string) => {
      const { data } = await api.post<UserDetailAdmin>(`/superadmin/users/${userId}/reactivate`);
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['superadmin'] });
    },
  });
}
